import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { validEmail, validPhoneCharacters } from '@propertypal/shared/src/utils/regex';
import Recaptcha, { getCaptchaKey } from '@propertypal/web-ui/src/security/Recaptcha';
import { openToast } from '@propertypal/web-ui/src/toast/ToastContext';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import * as yup from 'yup';
import Button from '../buttons/Button';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import TextArea from '../inputs/TextArea';
import TextInput from '../inputs/TextInput';
import { ContactContainer, ContactContent, ContactBox } from './ProductsContact.style';

const validationSchema = yup.object().shape({
  name: yup.string().trim().required('Please enter your name'),
  companyName: yup.string().required('Please enter your company name'),
  email: yup
    .string()
    .matches(validEmail, 'Please enter a valid email address')
    .required('Please enter your email address'),
  phoneNumber: yup
    .string()
    .matches(validPhoneCharacters, 'Please enter a valid phone number')
    .required('Please enter your phone number'),
  comments: yup.string().trim().required('Please enter some comments'),
});

interface ProductsContactProps {
  reason: string;
}

const ProductsContact: React.FC<ProductsContactProps> = ({ reason }) => {
  const [sent, setSent] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const theme = useTheme();

  const formik = useFormik({
    validationSchema,
    initialValues: {
      name: '',
      companyName: '',
      email: '',
      phoneNumber: '',
      comments: '',
    },
    onSubmit: async (values) => {
      if (!loading) {
        setLoading(true);

        try {
          const recaptcha = await getCaptchaKey('enquire');

          await axios(
            {
              method: 'POST',
              url: `${API_URL}/agent-enquire`,
              data: {
                name: values.name,
                company: values.companyName,
                email: values.email,
                telephone: values.phoneNumber,
                comments: values.comments,
                recaptcha,
                reason,
              },
            },
            dispatch,
          );

          setSent(true);
        } catch {
          setLoading(false);
          openToast({
            title: 'Error',
            message: "Something went wrong. We couldn't send your enquiry. Please try again!",
            type: 'error',
          });
        }
      }
    },
  });

  return (
    <>
      <Recaptcha />
      <ContactContainer id="contact-us">
        <ContactContent>
          <h2>Have any questions? We would love to hear from you.</h2>

          <ContactBox>
            <form onSubmit={formik.handleSubmit}>
              {!sent && (
                <>
                  <h4>Complete the form below and we&apos;ll be in touch as soon as we can</h4>

                  <TextInput
                    containerStyle={{ marginTop: 15 }}
                    value={formik.values.name}
                    onValueChange={formik.handleChange('name')}
                    name="name"
                    placeholder="Name"
                    error={formik.touched.name && formik.errors.name}
                  />

                  <TextInput
                    containerStyle={{ marginTop: 15 }}
                    value={formik.values.companyName}
                    onValueChange={formik.handleChange('companyName')}
                    name="companyName"
                    placeholder="Company Name"
                    error={formik.touched.companyName && formik.errors.companyName}
                  />

                  <TextInput
                    containerStyle={{ marginTop: 15 }}
                    value={formik.values.email}
                    onValueChange={formik.handleChange('email')}
                    name="email"
                    placeholder="Email"
                    error={formik.touched.email && formik.errors.email}
                  />

                  <TextInput
                    containerStyle={{ marginTop: 15 }}
                    value={formik.values.phoneNumber}
                    onValueChange={formik.handleChange('phoneNumber')}
                    name="phoneNumber"
                    placeholder="Phone"
                    error={formik.touched.phoneNumber && formik.errors.phoneNumber}
                  />

                  <TextArea
                    containerStyle={{ marginTop: 15 }}
                    placeholder="Comments"
                    name="comments"
                    value={formik.values.comments}
                    onValueChange={formik.handleChange('comments')}
                    rows={3}
                    error={formik.touched.comments && formik.errors.comments}
                  />

                  <Button containerStyle={{ marginTop: 15 }} loading={loading} type="submit">
                    Contact us today
                  </Button>
                </>
              )}

              {sent && (
                <>
                  <FontAwesomeIcon
                    icon={faPaperPlane}
                    style={{ margin: '0 auto 30px', fontSize: 50, display: 'block' }}
                    color={theme.textDark}
                  />
                  <h4>Your message has been sent.</h4>

                  <h6>A member of our team will be in touch as soon as possible.</h6>
                </>
              )}
            </form>
          </ContactBox>
        </ContactContent>
      </ContactContainer>
    </>
  );
};

export default ProductsContact;
